import {
  Button,
  ClientSelect,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  SupplierSelect,
  TagSelect,
  Tooltip,
  Typography,
} from 'components';
import React from 'react';
import { StatusTypes } from 'models/PurchaseOrder';
import { PERMISSIONS } from 'constants/permissions';
import { ResetIcon } from 'components/common/icons/ResetIcon';
import { useBrandTagsQuery, useSupplierTagsQuery } from 'api';
import { ItemSkuSelect } from 'components/common/itemSkuSelect';
import { BrandLockIcon, CalendarIcon, FilterIcon } from 'components/common/icons';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { ChooseSupplierModal } from 'pages/admin/purchaseOrders/add/chooseSupplierModal';
import { PreCreateCheckModal } from './preCreateCheckModal';
import styles from './../styles.module.scss';
import { useAddEdit } from './useAddEdit';
import { ItemsTable } from './itemsTable';
import { AddSupplierOfferModal } from 'pages/admin/supplierOffers/addSupplierOffer';
import { SUPPLIER_OFFER_SOURCE } from 'models/SupplierOffer';

const { Item } = Form;
const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export const AddEditPurchaseOrder = () => {
  const { hasAccess } = useAccessControl();
  const userId = JSON.parse(localStorage.getItem('userId') || 'null');
  const { data: brandTagData = [] } = useBrandTagsQuery();
  const { data: supplierTagData = [] } = useSupplierTagsQuery();

  const {
    form,
    onCancel,
    onValuesChange,
    brandIds,
    filters,
    onRowSelect,
    chooseSupplierModalState,
    onChooseSupplierModalStateChange,
    onCreateOrder,
    purchaseOrderItems,
    purchaseOrderItemIds,
    onReset,
    onPriceRequest,
    onAssign,
    onResetLockedBrands,
    brandsData,
    isAllSelectedBrandsLocked,
    onSupplierOfferModalStateChange,
    supplierOfferModalState,
    onBrandUnlock,
    hasLockedBrandByUser,
    preCreateCheckModalState,
    onPreCreateCheckModalStateChange,
    preCheckMessage,
  } = useAddEdit();

  return (
    <>
      <Row justify={'end'} gutter={[0, 32]} className={styles.purchase_request_add_main}>
        <Col span={24} className={styles.top_section}>
          <Row gutter={[0, 24]} justify={'center'}>
            <Col span={24}>
              <Typography.Title className={styles.add_edit_title} level={2}>
                Add Purchase Order
              </Typography.Title>
            </Col>
            <Col span={24}>
              <Collapse
                className={styles.collapse}
                accordion
                expandIconPosition="end"
                defaultActiveKey={'purchase-order-filter'}
                items={[
                  {
                    key: 'purchase-order-filter',
                    label: (
                      <Row align={'middle'} gutter={6} justify={'space-between'}>
                        <Col>
                          <Row align={'middle'} gutter={6}>
                            <FilterIcon color={'#05796B'} />
                            <Col>
                              <Text style={{ color: '#05796B' }} className={'font-weight-800 font-size-16'}>
                                Filter Orders
                              </Text>
                            </Col>
                          </Row>
                        </Col>
                        <Col>
                          <Button
                            size={'small'}
                            type={'text'}
                            variant={'text'}
                            className={'underline-button'}
                            onClick={onReset}
                          >
                            <Text underline>Reset</Text>
                          </Button>
                        </Col>
                      </Row>
                    ),
                    children: (
                      <Form onValuesChange={onValuesChange} requiredMark={false} layout={'vertical'} form={form}>
                        <Row gutter={[10, 20]}>
                          <Col span={6}>
                            <Item label={'Brand'} name={'brandIds'}>
                              <Select
                                mode={'multiple'}
                                maxTagCount={'responsive'}
                                dropdownRender={() => (
                                  <>
                                    <Divider />
                                    <Row align={'middle'} justify={'space-evenly'} style={{ padding: '6px' }}>
                                      <Button
                                        type={'text'}
                                        size={'small'}
                                        variant={'text'}
                                        icon={<ResetIcon />}
                                        style={{ color: '#525866' }}
                                        className="no-hover-bg font-size-14"
                                        onClick={onResetLockedBrands}
                                        disabled={!hasLockedBrandByUser}
                                      >
                                        Reset
                                      </Button>
                                      <Button
                                        type={'text'}
                                        size={'small'}
                                        variant={'text'}
                                        icon={<BrandLockIcon />}
                                        style={{ color: '#05796B' }}
                                        className="no-hover-bg font-size-14"
                                        onClick={onAssign}
                                        disabled={isAllSelectedBrandsLocked || !brandIds || !brandIds?.length}
                                      >
                                        Assign to Me
                                      </Button>
                                    </Row>
                                  </>
                                )}
                              >
                                {brandsData.map((_brand: any) => {
                                  return (
                                    <Option
                                      value={_brand.id}
                                      key={_brand.id}
                                      disabled={_brand.locked && userId !== _brand.lockedBy?.id}
                                    >
                                      <Tooltip
                                        title={
                                          _brand.locked && userId !== _brand.lockedBy?.id
                                            ? `Assigned: ${_brand.lockedBy?.firstName} ${_brand.lockedBy?.lastName}`
                                            : undefined
                                        }
                                        placement="left"
                                        getPopupContainer={(triggerNode) => triggerNode.parentElement!} // attaches tooltip to row
                                        classNames={{
                                          root: 'custom-tooltip',
                                        }}
                                      >
                                        <Row align="middle" className="gap-6" wrap={false}>
                                          {_brand.locked && (
                                            <Button
                                              onClick={(event) => {
                                                if (
                                                  hasAccess(PERMISSIONS.purchaseOrders.otherBrandUnassign.write) ||
                                                  userId === _brand.lockedBy?.id
                                                ) {
                                                  onBrandUnlock(_brand.id);
                                                  event.stopPropagation();
                                                }
                                              }}
                                              title={
                                                hasAccess(PERMISSIONS.purchaseOrders.otherBrandUnassign.write) ||
                                                userId === _brand.lockedBy?.id
                                                  ? 'Tap to unassign the brand'
                                                  : ''
                                              }
                                              icon={<BrandLockIcon />}
                                              size={'small'}
                                              type={'link'}
                                              variant={'text'}
                                            />
                                          )}
                                          <Text style={{ color: '#525866' }} ellipsis={true}>
                                            {_brand.name}
                                          </Text>
                                        </Row>
                                      </Tooltip>
                                    </Option>
                                  );
                                })}
                              </Select>
                            </Item>
                          </Col>
                          <Col span={6}>
                            <Item name="itemId" label={'Item SKU'}>
                              <ItemSkuSelect brandIds={brandIds} />
                            </Item>
                          </Col>
                          <Col span={6}>
                            <TagSelect data={brandTagData} name={'brandTags'} label={'Brand Tags'} size={'middle'} />
                          </Col>
                          <Col span={6}>
                            <Item name="cllientId" label={'Client'}>
                              <ClientSelect />
                            </Item>
                          </Col>
                          <Col span={6}>
                            <Item name={'supplierId'} label={'Supplier'}>
                              <SupplierSelect />
                            </Item>
                          </Col>
                          <Col span={6}>
                            <TagSelect
                              data={supplierTagData}
                              name={'supplierTags'}
                              label={'Supplier Tags'}
                              size={'middle'}
                            />
                          </Col>
                          <Col span={6}>
                            <Row gutter={12} wrap={false}>
                              <Col span={12}>
                                <Form.Item
                                  name="purchasePriceFrom"
                                  label={<span style={{ whiteSpace: 'nowrap' }}>Purchase Price Range</span>}
                                  rules={[
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        const dealAmountMax = getFieldValue('purchasePriceTo');
                                        const numValue = value ? Number(value) : NaN; // Convert value to a number
                                        const numDealAmountMax = dealAmountMax ? Number(dealAmountMax) : NaN; // Convert dealCountMin to a number

                                        if (numValue && numValue > numDealAmountMax) {
                                          return Promise.reject(
                                            new Error(
                                              'Min Purchase Price Range amount must be smaller than or equal to Max Purchase Price Range amount.'
                                            )
                                          );
                                        }
                                        // clearFieldValidation(form, 'dealAmountMax');
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <Input
                                    placeholder={'Min'}
                                    type={'number'}
                                    suffix={<Typography.Text type={'secondary'}>€</Typography.Text>}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={12}>
                                <Form.Item
                                  label={<span style={{ display: 'none' }}></span>}
                                  name="purchasePriceTo"
                                  rules={[
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        const dealAmountMin = getFieldValue('purchasePriceFrom');
                                        const numValue = value ? Number(value) : NaN; // Convert value to a number
                                        const numDealAmountMin = dealAmountMin ? Number(dealAmountMin) : NaN; // Convert dealCountMin to a number

                                        if (numValue && numValue < numDealAmountMin) {
                                          return Promise.reject(
                                            new Error(
                                              'Max Purchase Price Range amount must be greater than or equal to Min Purchase Price Range amount.'
                                            )
                                          );
                                        }
                                        // clearFieldValidation(form, 'dealAmountMin');
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <Input
                                    placeholder={'Max'}
                                    type={'number'}
                                    suffix={<Typography.Text type={'secondary'}>€</Typography.Text>}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={6}>
                            <Item name="itemStatus" label={'Item Status'}>
                              <Select options={StatusTypes} />
                            </Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item name="orderConfirmationDate" label={'Order Confirmation Date'}>
                              <RangePicker
                                suffixIcon={<CalendarIcon />}
                                className={'w-100'}
                                popupClassName={'fmg-range-picker'}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item name="deliveryDate" label={'Delivery Deadline'}>
                              <RangePicker
                                suffixIcon={<CalendarIcon />}
                                className={'w-100'}
                                popupClassName={'fmg-range-picker'}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form>
                    ),
                  },
                ]}
              />
            </Col>
            <Col span={24}>
              <ItemsTable filters={filters} onRowSelect={onRowSelect} />
            </Col>
          </Row>
        </Col>
        <Col span={24} className={styles.footer}>
          <Row justify={'end'}>
            <Col flex={'328px'}>
              <Row wrap={false} className="gap-8">
                <Button block size="large" onClick={onCancel} type="primary" ghost>
                  Cancel
                </Button>
                <Button
                  block
                  size="large"
                  onClick={onPriceRequest}
                  disabled={!purchaseOrderItemIds.length}
                  type="primary"
                  ghost
                >
                  Request Price
                </Button>
                <Button
                  block
                  size="large"
                  onClick={onSupplierOfferModalStateChange}
                  disabled={!purchaseOrderItemIds.length}
                  type="primary"
                  ghost
                >
                  Create Supplier Offer
                </Button>
                <Button
                  block
                  size="large"
                  disabled={!purchaseOrderItemIds.length}
                  type="primary"
                  onClick={onCreateOrder}
                >
                  Create Order
                </Button>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <ChooseSupplierModal
        onModalStateChange={onChooseSupplierModalStateChange}
        modalState={chooseSupplierModalState}
        purchaseOrderItemIds={purchaseOrderItemIds}
      />
      <AddSupplierOfferModal
        onModalStateChange={onSupplierOfferModalStateChange}
        modalState={supplierOfferModalState}
        values={purchaseOrderItems}
        type={'NONE'}
        source={SUPPLIER_OFFER_SOURCE.PO_ADD}
      />
      <PreCreateCheckModal
        onModalStateChange={onPreCreateCheckModalStateChange}
        modalState={preCreateCheckModalState}
        preCheckMessage={preCheckMessage}
        onContinue={onChooseSupplierModalStateChange}
        purchaseOrderItemIds={purchaseOrderItemIds}
      />
    </>
  );
};
